import type { ProductModificationFormResult } from 'models/forms/productModification';
import { defaultFormReceivedThunk, dynamicFormReloadedThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonFormActions } from 'state/forms/actions/form';
import { productModificationSelectors } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { dateNow } from 'utils/core/now';

import {
  productModificationApplyRequested,
  productModificationFormReceived,
  productModificationFormRequestFailed,
  productModificationFormRequestTimedOut,
  productModificationReloadRequested,
  productModificationTabCreated,
} from './productModificationForm.action';

export function productModificationTabCreatedThunk(productId: string, tabName: string): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    const tabId = getNewGuid();
    dispatch(productModificationTabCreated(tabId, productId, tabName));
  };
}

export function productModificationFormRequestFailedThunk(
  tabId: string,
  productId: string,
  error: string,
  traceId: string | undefined,
): Thunk<void> {
  return (dispatch, _getState) => {
    dispatch(productModificationFormRequestFailed(tabId, productId, error));
    dispatch(commonFormActions.defaultFormFailed('dynamic', tabId, error, traceId));
  };
}

export function productModificationFormRequestTimedOutThunk(tabId: string, productId: string): Thunk<void> {
  return (dispatch, _getState) => {
    dispatch(productModificationFormRequestTimedOut(tabId, productId));
    dispatch(commonFormActions.defaultFormTimeouted('dynamic', tabId));
  };
}

export function productModificationFormResultReceivedThunk(
  tabId: string,
  productId: string,
  productModificationFormResult: ProductModificationFormResult,
  isReloading: boolean,
): Thunk<void> {
  return (dispatch, _getState) => {
    dispatch(productModificationFormReceived(tabId, productId, productModificationFormResult));
    const dynamicFormResult = productModificationFormResult.form;

    const {
      blocksMetadata,
      fieldsMetadata,
      productDescription,
      values,
      version,
      negotiationId,
      actions,
      quoteQuery,
      errors,
      etag,
      sendWholeFormData,
      allowedRequests,
    } = dynamicFormResult;

    dispatch(
      isReloading
        ? dynamicFormReloadedThunk(
            tabId,
            blocksMetadata,
            fieldsMetadata,
            productDescription,
            values,
            version,
            negotiationId,
            actions,
            quoteQuery,
            allowedRequests,
            errors,
            etag,
            sendWholeFormData,
          )
        : defaultFormReceivedThunk(
            tabId,
            blocksMetadata,
            fieldsMetadata,
            productDescription,
            values,
            version,
            negotiationId,
            actions,
            quoteQuery,
            allowedRequests,
            errors,
            sendWholeFormData,
            etag,
          ),
    );
  };
}

export function productModificationApplyRequestedThunk(tabId: string, durationInMillis: number): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    dispatch(productModificationApplyRequested(tabId, getNewGuid(), dateNow(), durationInMillis));
  };
}

export function productModificationReloadRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const productId = productModificationSelectors.getProductId(state, tabId)!;
    dispatch(productModificationReloadRequested(tabId, productId));
  };
}
